/**
 * @fileoverview India locale utilities — INR currency, Indian number system
 * All monetary values in the system are stored as full integers (rupees).
 */

/**
 * Format a number in the Indian numbering system (lakhs & crores).
 * @param {number} amount - Amount in rupees
 * @param {{ showSymbol?: boolean, decimals?: number }} [opts]
 * @returns {string}
 */
export function formatINR(amount, { showSymbol = true, decimals = 0 } = {}) {
  if (amount === null || amount === undefined || isNaN(amount)) return "—";
  const sym = showSymbol ? "₹" : "";
  return (
    sym +
    Math.abs(amount).toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  );
}

/**
 * Compact format: ₹1.25 Cr  |  ₹45.00 L  |  ₹9,500
 * @param {number} amount
 * @param {{ showSymbol?: boolean }} [opts]
 * @returns {string}
 */
export function formatCompact(amount, { showSymbol = true } = {}) {
  if (amount === null || amount === undefined || isNaN(amount)) return "—";
  const sym = showSymbol ? "₹" : "";
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  if (abs >= 1_00_00_000) return `${sign}${sym}${(abs / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000)    return `${sign}${sym}${(abs / 1_00_000).toFixed(2)} L`;
  return `${sign}${sym}${abs.toLocaleString("en-IN")}`;
}

/**
 * Format as lakhs string: "45.00 L"
 * @param {number} amount
 * @returns {string}
 */
export function toLakhs(amount) {
  return `${(amount / 1_00_000).toFixed(2)} L`;
}

/**
 * Format as crores string: "1.25 Cr"
 * @param {number} amount
 * @returns {string}
 */
export function toCrores(amount) {
  return `${(amount / 1_00_00_000).toFixed(2)} Cr`;
}

/**
 * Format a date in Indian standard: DD MMM YYYY
 * @param {string} dateStr
 * @returns {string}
 */
export function formatDateIN(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Current ISL season label */
export const SEASON = "2025/26";

/**
 * Club name — TODO: replace with API fetch: GET /api/v1/club/config
 * Set here as a single source of truth for the entire app.
 */
export const CLUB_NAME = "ClubOS";

/** League name */
export const LEAGUE = "Indian Super League (ISL)";
